import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc, Firestore } from 'firebase/firestore';

export interface AdminState {
  isAdmin: boolean;
  adminLoading: boolean;
  view: 'calendar' | 'admin';
  setView: (view: 'calendar' | 'admin') => void;
}

/**
 * Hook to manage admin state based on Firestore /admins collection.
 * No more PIN - admin status is determined by whether the user's UID
 * exists in the /admins collection.
 */
export const useAdmin = (db: Firestore | null, user: User | null, isGoogleUser: boolean): AdminState => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [view, setView] = useState<'calendar' | 'admin'>('calendar');

  // Check admin status whenever user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      // Only Google users can be admins
      if (!db || !user || !isGoogleUser) {
        setIsAdmin(false);
        setView('calendar');
        return;
      }

      setAdminLoading(true);
      
      try {
        // Check if user's UID exists in /admins collection
        const adminDocRef = doc(db, 'admins', user.uid);
        const adminDoc = await getDoc(adminDocRef);
        
        const adminStatus = adminDoc.exists();
        setIsAdmin(adminStatus);
        
        // Auto-switch to admin view if user is admin
        if (adminStatus) {
          setView('admin');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setAdminLoading(false);
      }
    };

    checkAdminStatus();
  }, [db, user, isGoogleUser]);

  // Wrap setView to prevent non-admins from accessing admin view
  const safeSetView = useCallback((newView: 'calendar' | 'admin') => {
    if (newView === 'admin' && !isAdmin) {
      console.warn('Attempted to access admin view without admin privileges');
      return;
    }
    setView(newView);
  }, [isAdmin]);

  return { 
    isAdmin, 
    adminLoading,
    view, 
    setView: safeSetView
  };
};
